import * as THREE from "three";
import type { KnightActor } from "./KnightActor";

/**
 * Stand-in for the production GLB knight (section 24), built as an
 * extruded chess-knight silhouette — the classic recognizable side profile
 * (mane, ear, brow, nose bridge, jaw) — rather than a stack of unrelated
 * primitives. One `Group`, one shared `MeshStandardMaterial`, one base
 * `CylinderGeometry`, one head/neck `ExtrudeGeometry` — all created once
 * and reused for the lifetime of the scene, never recreated per tour or
 * per move.
 *
 * Orientation: the silhouette is authored with its nose along the shape's
 * local +X and extruded along local +Z (`ExtrudeGeometry` always extrudes
 * a 2D XY shape along Z). `buildKnightHeadGeometry` then rotates the
 * finished geometry -90° around Y once, at construction time, so the nose
 * ends up along local +Z — the axis `PlaybackController` treats as
 * "forward" (see `JumpAnimation.sampleJumpPose`'s `atan2(dx, dz)` yaw) —
 * while the silhouette's flat, most-recognizable face ends up looking
 * sideways along local X. That matches how a knight chess piece is
 * conventionally recognized: in profile, not head-on.
 */
export class PlaceholderKnight implements KnightActor {
  readonly object: THREE.Group;
  private material: THREE.MeshStandardMaterial;
  private baseGeometry: THREE.CylinderGeometry;
  private headGeometry: THREE.ExtrudeGeometry;

  constructor() {
    this.object = new THREE.Group();
    this.object.name = "PlaceholderKnight";

    this.material = new THREE.MeshStandardMaterial({
      color: 0xd2a84d,
      roughness: 0.28,
      metalness: 0.58,
    });

    this.baseGeometry = new THREE.CylinderGeometry(0.22, 0.3, 0.14, 12);
    const base = new THREE.Mesh(this.baseGeometry, this.material);
    base.position.y = 0.07;

    this.headGeometry = buildKnightHeadGeometry();
    const head = new THREE.Mesh(this.headGeometry, this.material);
    head.position.y = 0.14; // sits directly on top of the base

    const collarGeometry = new THREE.CylinderGeometry(.15, .21, .13, 10);
    const collar = new THREE.Mesh(collarGeometry, this.material);
    collar.position.y = .19;

    for (const mesh of [base, collar, head]) {
      mesh.castShadow = true;
      mesh.receiveShadow = false;
      this.object.add(mesh);
    }
    collarGeometry.dispose = collarGeometry.dispose.bind(collarGeometry);
  }

  setPosition(position: THREE.Vector3): void {
    this.object.position.copy(position);
  }

  setYaw(yawRadians: number): void {
    this.object.rotation.y = yawRadians;
  }

  dispose(): void {
    this.material.dispose();
    this.baseGeometry.dispose();
    this.headGeometry.dispose();
    this.object.clear();
  }
}

function buildKnightHeadGeometry(): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  // Back of the neck, up through three small mane notches.
  shape.moveTo(-0.14, 0.0);
  shape.lineTo(-0.16, 0.1);
  shape.lineTo(-0.1, 0.16);
  shape.lineTo(-0.16, 0.22);
  shape.lineTo(-0.09, 0.3);
  shape.lineTo(-0.15, 0.36);
  shape.lineTo(-0.06, 0.44);
  // Poll (top of head, between the ears) and the ear.
  shape.quadraticCurveTo(-0.02, 0.52, 0.04, 0.56);
  shape.lineTo(0.02, 0.6);
  shape.lineTo(0.1, 0.72);
  shape.lineTo(0.13, 0.58);
  // Forehead, nose bridge, nostril, mouth.
  shape.quadraticCurveTo(0.2, 0.54, 0.27, 0.5);
  shape.quadraticCurveTo(0.36, 0.47, 0.4, 0.4);
  shape.quadraticCurveTo(0.38, 0.34, 0.3, 0.32);
  shape.lineTo(0.22, 0.24);
  // Jaw and throat back down to the base.
  shape.quadraticCurveTo(0.16, 0.16, 0.12, 0.08);
  shape.lineTo(0.08, 0.0);
  shape.closePath();

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: 0.16,
    bevelEnabled: true,
    bevelThickness: 0.015,
    bevelSize: 0.015,
    bevelSegments: 2,
    curveSegments: 8,
  });
  geometry.translate(0, 0, -0.08); // center the extrusion depth around local 0
  geometry.rotateY(-Math.PI / 2); // bake in: nose -> local +Z ("forward")
  return geometry;
}
